import { NavigateBefore, NavigateNext } from "@mui/icons-material";
import {
  alpha,
  Button,
  Skeleton,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import Viewer from "components/Viewer/Viewer";
import VirtualTourHeader from "components/VirtualTourHeader";
import PCLDViewerHeader from "containers/PointCloudViewer/components/PointCloudViewerHeader";
import { useLoading } from "contexts/LoadingContext";
import { Suspense, useMemo } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import Toolbar from "./Toolbar";
import ViewerWrapperLoader from "./ViewerWrapperLoader";
import FullScreenInit from "components/FullScreenInit";
import GSplatViewerHeader from "containers/GaussianSplatViewer/GSplatViewerHeader";

interface SearchParams {
  type: "vtour" | "ptcld" | "gsplat";
  mode: "uniscan" | "uniscan-compare" | "biscan";
}

const CustomButton = styled(Button)(({ theme }) => ({
  backgroundColor: "background.default",
  color: "white",
  border: 0,
  px: 0,
  minWidth: "32px",
  width: "32px",
  "&.Mui-disabled": {
    backgroundColor: "background.default",
    border: 0,
  },
}));

const ViewerWrapper = () => {
  const [queryParams, setQueryParams] = useSearchParams();
  const mode = (queryParams.get("mode") as SearchParams["mode"]) || "uniscan";
  const type = (queryParams.get("type") as SearchParams["type"]) || "vtour";
  const { scanId = "" } = useParams();
  const scan1Id = queryParams.get("scan1") || scanId;
  const scan2Id = queryParams.get("scan2") || "";

  const {
    setBlurryEffect,
    scan1Wrapper,
    scan2Wrapper,
    parentRef,
    fullscreen,
    scans1Loading,
    scans1Error,
    scans2Loading,
    scans2Error,
    scan1,
    scan2,
    scan1Data,
  } = useLoading();

  const location = useLocation();
  const isPublic = useMemo(() => {
    return location.pathname.startsWith("/public");
  }, [location.pathname]);

  const handlePrevScanFirst = () => {
    setBlurryEffect(true);
    scan1Wrapper.handlePrevScan();
  };

  const handleNextScanFirst = () => {
    setBlurryEffect(true);
    scan1Wrapper.handleNextScan();
  };

  const handlePrevScanSecond = () => {
    setBlurryEffect(true);
    scan2Wrapper.handlePrevScan();
  };

  const handleNextScanSecond = () => {
    setBlurryEffect(true);
    scan2Wrapper.handleNextScan();
  };

  if (scans1Error && scans2Error) {
    return (
      <FullScreenInit
        displayText={scans1Error?.message + scans2Error?.message}
        hideBackButton
        height="100%"
        animation={"error"}
      />
    );
  }

  return (
    <Stack
      p={2}
      height="100%"
      sx={{ overflowX: "hidden" }}
      direction="column"
      gap={{ xs: 0.5, sm: 1 }}
    >
      {type === "vtour" ? (
        <VirtualTourHeader
          headingName={"Virtual Tour Viewer"}
          scanName={scan1Data?.description || ""}
        />
      ) : type === "ptcld" ? (
        <PCLDViewerHeader />
      ) : type === "gsplat" ? (
        <GSplatViewerHeader />
      ) : null}
      <Stack
        height={"100%"}
        ref={parentRef}
        sx={(theme) => ({
          borderRadius: 2,
          background: alpha(theme.palette.background.paper, 0.05),
          backdropFilter: "blur(10px)",
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          boxShadow: theme.shadows[3],
          transition: theme.transitions.create(["box-shadow", "border-color"], {
            duration: theme.transitions.duration.standard,
          }),
          "&:hover": {
            boxShadow: theme.shadows[8],
            borderColor: alpha(theme.palette.divider, 0.4),
            background: alpha(theme.palette.background.paper, 0.1),
          },
        })}
      >
        {scans1Loading || scans2Loading ? (
          <ViewerWrapperLoader />
        ) : (
          <>
            {((fullscreen &&
              (type === "ptcld" || mode === "uniscan-compare")) ||
              !fullscreen) && <Toolbar />}
            <Stack
              sx={{
                flex: 1,
                borderRadius: 2,
                borderColor: "divider",
                position: "relative",
                height: "100%",
              }}
              // id="viewer"
            >
              <Suspense
                fallback={
                  <Skeleton
                    height="100%"
                    sx={{
                      transform: "none",
                      WebkitTransform: "none",
                    }}
                  />
                }
              >
                {(scan1 || scan2) && <Viewer />}
              </Suspense>
              <Stack
                sx={{
                  flexDirection: "row",
                  position: "absolute",
                  bottom: "4%",
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Stack
                  sx={{
                    backgroundColor: "background.paper",
                    gap: 1,
                    borderRadius: 1,
                    flexDirection: "row",
                    alignItems: "center",
                    width: "auto",
                    transform: mode === "uniscan" ? "none" : "translateX(50%)",
                  }}
                >
                  <CustomButton
                    aria-label="prevscan"
                    size={"large"}
                    onClick={handlePrevScanFirst}
                    variant="outlined"
                    disabled={!scan1Wrapper.hasPrev}
                  >
                    <NavigateBefore fontSize="small" />
                  </CustomButton>
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                    }}
                  >
                    {scan1Wrapper.dateText}
                  </Typography>
                  <CustomButton
                    aria-label="nextscan"
                    size={"large"}
                    variant="outlined"
                    onClick={handleNextScanFirst}
                    disabled={!scan1Wrapper.hasNext}
                  >
                    <NavigateNext fontSize="small" />
                  </CustomButton>
                </Stack>
                {!(mode === "uniscan") && (
                  <>
                    <Stack width={"50%"}></Stack>
                    <Stack
                      sx={{
                        backgroundColor: "background.paper",
                        gap: 1,
                        borderRadius: 1,
                        flexDirection: "row",
                        alignItems: "center",
                        transform: "translateX(-50%)",
                      }}
                    >
                      <CustomButton
                        aria-label="prevscan"
                        size={"medium"}
                        onClick={handlePrevScanSecond}
                        variant="outlined"
                        disabled={!scan2Wrapper?.hasPrev}
                      >
                        <NavigateBefore fontSize="small" />
                      </CustomButton>
                      <Typography
                        sx={{
                          fontSize: "0.9rem",
                        }}
                      >
                        {scan2Wrapper?.dateText}
                      </Typography>
                      <CustomButton
                        aria-label="nextscan"
                        size={"medium"}
                        variant="outlined"
                        onClick={handleNextScanSecond}
                        disabled={!scan2Wrapper?.hasNext}
                      >
                        <NavigateNext fontSize="small" />
                      </CustomButton>
                    </Stack>
                  </>
                )}
              </Stack>
            </Stack>
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default ViewerWrapper;
